import { C } from "@hikari/vault-core";

const Para = ({ children }) => (
    <p
        style={{
            color: C.muted,
            fontSize: "0.92rem",
            lineHeight: 1.75,
            margin: "0 0 1rem 0",
            fontFamily: C.font,
        }}
    >
        {children}
    </p>
);
const H = ({ children }) => (
    <h3
        style={{
            color: C.text,
            fontSize: "1.05rem",
            fontWeight: 500,
            margin: "2rem 0 0.75rem",
            fontFamily: C.font,
        }}
    >
        {children}
    </h3>
);

export default function Terms() {
    return (
        <main
            style={{
                maxWidth: "720px",
                margin: "0 auto",
                padding: "4rem 1.5rem 5rem",
                fontFamily: C.font,
            }}
        >
            <div
                style={{
                    fontSize: "0.72rem",
                    letterSpacing: "0.3em",
                    color: C.accent,
                    marginBottom: "0.75rem",
                }}
            >
                LEGAL
            </div>
            <h1
                style={{
                    fontSize: "clamp(1.8rem, 4vw, 2.6rem)",
                    fontWeight: 500,
                    margin: "0 0 0.5rem",
                    color: C.text,
                }}
            >
                Terms of service
            </h1>
            <div style={{ color: C.dim, fontSize: "0.82rem", marginBottom: "2.5rem" }}>
                Last updated: {new Date().toISOString().slice(0, 10)} · Effective immediately
            </div>

            <Para>
                <strong style={{ color: C.text }}>The short version.</strong> Use Hikari Vault for
                anything legal in your jurisdiction. We provide the service as-is and cannot
                recover your data if you lose your recovery phrase. Don't try to abuse the service
                or use it to host content that is illegal where you live. The longer version is
                below.
            </Para>

            <H>1. Acceptance</H>
            <Para>
                By creating a vault, you agree to these terms. If you don't agree, don't create
                one. You can stop using the service at any time by wiping your vault in Settings.
            </Para>

            <H>2. The service</H>
            <Para>
                Hikari Vault is a zero-knowledge encrypted storage service operated by Hikari Brand.
                We provide: client-side encryption tooling, an encrypted-blob sync service, and
                (for Plus users) additional features such as multi-device sync, file attachments,
                and Shamir secret-sharing setup.
            </Para>

            <H>3. Your responsibilities</H>
            <Para>
                You are responsible for keeping your recovery phrase safe. We literally cannot help
                you if you lose it — the architecture does not permit it. You agree to use one or
                more of the recovery layers we provide (written phrase, encrypted backup file,
                printed recovery card, Shamir trustees) and to test your recovery setup at least
                once before storing irreplaceable information.
            </Para>
            <Para>
                You are responsible for the legality of what you store in your vault under the laws
                that apply to you. Because we cannot see your content, we cannot screen for
                illegal material — but that does not absolve you of the obligation not to store
                it.
            </Para>

            <H>4. Prohibited uses</H>
            <Para>
                You may not use the service to: (a) attack, probe, scan, or attempt to compromise
                our infrastructure; (b) circumvent rate limits or licence-enforcement code; (c)
                resell, sublicense, or commercially redistribute the hosted service without prior
                written agreement (self-hosting your own instance under the open-source licence is
                explicitly permitted); (d) use the service in any way that violates applicable law
                in your jurisdiction.
            </Para>

            <H>5. Payments and refunds</H>
            <Para>
                Plus licences are purchased with cryptocurrency for fixed terms (monthly, yearly,
                or lifetime). Because crypto transactions are non-reversible, refund requests are
                handled on a case-by-case basis and only within 14 days of purchase. To request a
                refund, contact <code>support@hikarivault.app</code> with your licence code; we
                will arrange a manual crypto refund to a wallet of your choice.
            </Para>

            <H>6. Service availability</H>
            <Para>
                We aim for high uptime but do not promise specific availability. Because your vault
                is also cached locally on your devices, brief service outages do not lock you out
                of your data — you can still read your vault offline. If the service were to be
                discontinued, we would provide at least 90 days' notice and a tool to export your
                full encrypted vault as a portable file.
            </Para>

            <H>7. Account termination</H>
            <Para>
                We may terminate or suspend service for any vault that we have credible evidence is
                being used for prohibited purposes (Section 4), or to comply with a valid legal
                order. Because we cannot read vault contents, such determinations are based on
                request volume, payment fraud signals, or external information — not content
                inspection.
            </Para>

            <H>8. Disclaimer of warranties</H>
            <Para>
                The service is provided “AS IS” and “AS AVAILABLE” without warranties of any
                kind, express or implied, including merchantability, fitness for a particular
                purpose, or non-infringement. We do not warrant uninterrupted operation,
                error-free behaviour, or absolute resistance to all forms of attack. We commission
                independent security audits and publish the results; treat the audit reports, not
                this clause, as the meaningful indicator of security posture.
            </Para>

            <H>9. Limitation of liability</H>
            <Para>
                To the maximum extent permitted by law, Hikari Brand's total liability to you for
                any claim arising from your use of the service is limited to the amount you have
                paid us in the twelve months preceding the claim, or USD 100, whichever is
                greater. We are not liable for indirect, incidental, special, consequential, or
                punitive damages, including loss of data resulting from your loss of recovery
                phrase.
            </Para>

            <H>10. Open source</H>
            <Para>
                The Hikari Vault backend and frontend will be released under the MIT licence on
                public GitHub at or before v1 launch (M5). You are permitted and encouraged to
                self-host your own instance. These terms apply only to the hosted service operated
                by Hikari Brand at <code>hikarivault.app</code>.
            </Para>

            <H>11. Changes to these terms</H>
            <Para>
                We may update these terms; the current version will always be at this URL with a
                new “last updated” date. Material changes will be surfaced in the app where
                practical. Continued use after a change constitutes acceptance.
            </Para>

            <H>12. Governing law</H>
            <Para>
                These terms are governed by the laws of the jurisdiction in which Hikari Brand is
                registered, without regard to conflict-of-law rules. Where local consumer
                protection law gives you stronger rights, those rights apply.
            </Para>

            <H>13. Contact</H>
            <Para>
                Legal: <code>legal@hikarivault.app</code> · Support:{" "}
                <code>support@hikarivault.app</code>.
            </Para>
        </main>
    );
}
